/**
 * The document a guide body is previewed inside.
 *
 * Its own document, with its own :root, and that is the point. A guide body is styled by inline
 * `var(--token)` references, and those resolve against whatever document they are rendered in.
 * Previewing inside the Control Center would resolve them against the console's dark theme and
 * show an operator colours the public page will never use.
 *
 * The values below are therefore the PUBLIC SITE's, written out as literals. There is no way around
 * that: the console does not have the public palette, and an iframe cannot inherit custom properties
 * across the boundary. This is the same reason `emailLayout.ts` carries literals, and this file is
 * exempt from the colour-literal guardrail for the same reason.
 *
 * Only the tokens a guide body may name are declared. A token that is missing here renders unstyled
 * in the preview, which is exactly what it would do on the public page.
 */
export function guidePreviewDocument(body: string): string {
  return `<!doctype html>
<html><head><meta charset="utf-8" />
<style>
  :root {
    --color-text: #212121;
    --color-text-muted: #757575;
    --color-surface: #FFFFFF;
    --color-background: #F5F5F5;
    --color-border: #E0E0E0;
    --color-primary: #0369A1;
    --color-accent: #0369A1;
    --color-teal: #0D9488;
    --color-teal-lt: #E0FAFA;
    --color-gold: #B7791F;
    --color-semantic-success: #2E7D32;
    --color-success-bg: #E8F5E9;
    --color-warning-bg: #FFF3E0;
    --color-red: #C62828;
    --color-red-lt: #FFEBEE;
    --color-badge-bg: #E0F2FE;
    --fs-xs: 13px; --fs-sm: 15px; --fs-base: 16px;
    --fs-xl: 18px; --fs-2xl: 20px; --fs-3xl: 24px;
    --space-1: 4px; --space-2: 8px; --space-3: 12px; --space-4: 16px;
    --space-6: 24px; --space-8: 32px; --space-12: 48px;
    --radius-base: 4px; --radius-btn: 6px; --radius-card: 12px;
    --radius-modal: 16px; --radius-chip: 9999px;
    --shadow-sm: 0 1px 4px rgba(3,105,161,0.08);
    --shadow-base: 0 2px 8px rgba(3,105,161,0.10);
    --shadow-md: 0 4px 16px rgba(3,105,161,0.12);
    --font-family-display: Montserrat, sans-serif;
    --font-family-secondary: Lato, sans-serif;
    --font-family-mono: "DM Mono", monospace;
    --font-weight-normal: 400; --font-weight-bold: 700;
  }
  body {
    margin: 0; padding: 24px;
    background: var(--color-background); color: var(--color-text);
    font-family: var(--font-family-secondary); font-size: var(--fs-base); line-height: 1.7;
  }
  h2, h3, h4 { font-family: var(--font-family-display); }
  h2 { font-size: var(--fs-2xl); margin: 32px 0 12px; }
  h3 { font-size: var(--fs-xl); margin: 24px 0 8px; }
  img, iframe { max-width: 100%; }
  iframe { aspect-ratio: 16 / 9; width: 100%; border: 0; }
  table { border-collapse: collapse; width: 100%; font-size: var(--fs-sm); }
  th, td { border: 1px solid var(--color-border); padding: 8px 12px; text-align: left; }
  th { background: var(--color-surface); }
</style>
</head><body>${body}</body></html>`;
}

